import { Col, Row } from 'antd'
import { useFormik } from 'formik'
import { Icon } from '@iconify/react'
import { useRef } from 'react'
import dayjs from 'dayjs'

import { Button } from 'common/components/Button/Button'
import { DatePicker } from 'common/components/DatePicker/DatePicker'
import { Input } from 'common/components/Input/Input'
import { Select } from 'common/components/Select/Select'
import { Textarea } from 'common/components/Textarea/Textarea'
import PhoneInput from 'common/components/PhoneInput/PhoneInput'
import { BUTTON_MODIFIER, BUTTON_PROPORTION } from 'common/constants/buttonConstants'
import { DATE_FORMAT } from 'common/constants/dateFormatConstants'
import { SpinnerOverlay } from 'common/components/SpinnerOverlay'
import { useFetchCountriesQuery, useFetchStatesQuery } from 'common/store/api/rootApi'
import { useParams } from 'react-router'
import { useFetchEncounterPatientsQuery } from 'features/Procedure/state/procedure.api'

import styles from './patient-info-dialog.module.scss'
import { ReferralSourceField } from './referral-source-field'
import {
  patientContactSchema,
  toCountrySelectOptions,
  toStateSelectOptions,
  US_COUNTRY_CODE,
  type PatientContactFormValues,
} from './patient-contact-form.schema'
import {
  getApplicableDiscountFields,
  resolveCategoryChange,
  toDiscountCategoryOptions,
  toDiscountTypeOptions,
  type DiscountTypeValues,
} from './patient-discount'

type Props = {
  initialValues: PatientContactFormValues
  isSaving: boolean
  onBack: () => void
  onSubmit: (values: PatientContactFormValues) => Promise<void>
}

const DOB_DISPLAY_FORMAT = 'MM/DD/YYYY'

export const PatientContactForm = ({ initialValues, isSaving, onBack, onSubmit }: Props) => {
  const { siteId } = useParams<{ siteId: string }>()
  const { data: countries, isFetching: isFetchingCountries } = useFetchCountriesQuery()
  const { data: states, isFetching: isFetchingStates } = useFetchStatesQuery()
  const { data: encounterPatients } = useFetchEncounterPatientsQuery(
    { siteId: siteId! },
    { skip: !siteId },
  )
  const countryOptions = toCountrySelectOptions(countries)
  const stateOptions = toStateSelectOptions(states)
  const discountCategoryOptions = toDiscountCategoryOptions(encounterPatients?.discount_categories)
  const discountTypeOptions = toDiscountTypeOptions(encounterPatients?.discount_type)

  const { values, errors, handleChange, handleSubmit, setFieldValue } =
    useFormik<PatientContactFormValues>({
      initialValues,
      validationSchema: patientContactSchema,
      enableReinitialize: true,
      validateOnChange: true,
      onSubmit: async (formValues) => {
        await onSubmit(formValues)
      },
    })

  const isUsCountry = values.country === US_COUNTRY_CODE

  const handleCountryChange = (value?: string) => {
    setFieldValue('country', value ?? '')
    if (value !== US_COUNTRY_CODE) {
      setFieldValue('state', '')
    }
  }

  const applicableDiscount = getApplicableDiscountFields(values.discount_category)

  // Remembers a type value that gets hidden when the category changes, so it can
  // be restored when the category returns to one where that context applies again.
  const stashedDiscountRef = useRef<DiscountTypeValues>({ products: null, procedures: null })

  const handleDiscountCategoryChange = (value?: string) => {
    const nextCategory = value ?? null
    const { next, stash } = resolveCategoryChange({
      prevCategory: values.discount_category ?? null,
      nextCategory,
      current: {
        products: values.discount_type_products ?? null,
        procedures: values.discount_type_procedures ?? null,
      },
      stash: stashedDiscountRef.current,
    })
    stashedDiscountRef.current = stash
    setFieldValue('discount_category', nextCategory)
    setFieldValue('discount_type_products', next.products)
    setFieldValue('discount_type_procedures', next.procedures)
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form} noValidate>
      <SpinnerOverlay isLoading={isSaving} />
      <h2 className={styles.formTitle}>Edit Patient</h2>

      <Row gutter={[12, 8]}>
        <Col xs={24} md={8}>
          <DatePicker
            label='Date of Birth'
            value={values.dob ? dayjs(values.dob) : null}
            onChange={(date) =>
              setFieldValue('dob', date ? date.format(DATE_FORMAT.PATIENT_TRACKER_DATE) : '')
            }
            format={DOB_DISPLAY_FORMAT}
            placeholder='Select date of birth'
            disabledDate={(current) => !!current && current.isAfter(dayjs(), 'day')}
            error={errors.dob}
          />
        </Col>
        <Col xs={24} md={8}>
          <PhoneInput
            label='Phone'
            value={values.phone ?? ''}
            onChange={(value) => setFieldValue('phone', value ?? '')}
            error={errors.phone}
          />
        </Col>
        <Col xs={24} md={8}>
          <Input
            label='Email'
            name='email'
            value={values.email ?? ''}
            onChange={handleChange}
            error={errors.email}
          />
        </Col>

        <Col xs={24} md={12}>
          <Input
            label='Address'
            name='address'
            value={values.address ?? ''}
            onChange={handleChange}
            error={errors.address}
          />
        </Col>
        <Col xs={24} md={12}>
          <ReferralSourceField
            value={values.referral_source ?? ''}
            onChange={(value) => setFieldValue('referral_source', value)}
            error={errors.referral_source}
          />
        </Col>

        <Col xs={24} md={7}>
          <Input
            label='City'
            name='city'
            value={values.city ?? ''}
            onChange={handleChange}
            error={errors.city}
          />
        </Col>
        <Col xs={24} md={5}>
          <Select
            label='State'
            value={values.state || undefined}
            onChange={(value) => setFieldValue('state', value ?? '')}
            listOptions={stateOptions}
            loading={isFetchingStates}
            disabled={!isUsCountry}
            showSearch
            placeholder='Select state'
            error={errors.state}
          />
        </Col>
        <Col xs={24} md={4}>
          <Input
            label='Zip Code'
            name='zip_code'
            value={values.zip_code ?? ''}
            onChange={handleChange}
            error={errors.zip_code}
          />
        </Col>
        <Col xs={24} md={8}>
          <Select
            label='Country'
            value={values.country || undefined}
            onChange={handleCountryChange}
            listOptions={countryOptions}
            loading={isFetchingCountries}
            showSearch
            allowClear
            placeholder='Select country'
            error={errors.country}
          />
        </Col>

        <Col xs={24} md={8}>
          <Select
            label='Discount Category'
            value={values.discount_category || undefined}
            onChange={handleDiscountCategoryChange}
            listOptions={discountCategoryOptions}
            allowClear
            placeholder='Select discount category'
          />
        </Col>
        {applicableDiscount.products && (
          <Col xs={24} md={8}>
            <Select
              label='Products'
              value={values.discount_type_products || undefined}
              onChange={(value) => setFieldValue('discount_type_products', value ?? null)}
              listOptions={discountTypeOptions}
              allowClear
              placeholder='Select products discount'
            />
          </Col>
        )}
        {applicableDiscount.procedures && (
          <Col xs={24} md={8}>
            <Select
              label='Procedures'
              value={values.discount_type_procedures || undefined}
              onChange={(value) => setFieldValue('discount_type_procedures', value ?? null)}
              listOptions={discountTypeOptions}
              allowClear
              placeholder='Select procedures discount'
            />
          </Col>
        )}

        <Col xs={24}>
          <Textarea
            label='Billing Note'
            name='billing_note'
            value={values.billing_note ?? ''}
            onChange={(event) => setFieldValue('billing_note', event.target.value)}
            autoSize={{ minRows: 1, maxRows: 4 }}
          />
        </Col>
        <Col xs={24}>
          <Textarea
            label='General Note'
            name='general_note'
            value={values.general_note ?? ''}
            onChange={(event) => setFieldValue('general_note', event.target.value)}
            autoSize={{ minRows: 1, maxRows: 4 }}
          />
        </Col>
      </Row>

      <div className={styles.formFooter}>
        <Button
          htmlType='button'
          modifier={BUTTON_MODIFIER.SECONDARY}
          proportion={BUTTON_PROPORTION.SMALL}
          disabled={isSaving}
          onClick={onBack}>
          <span className={styles.backButtonContent}>
            <Icon icon='lucide:arrow-left' width={18} height={18} />
            Back
          </span>
        </Button>
        <Button
          htmlType='submit'
          modifier={BUTTON_MODIFIER.PRIMARY}
          proportion={BUTTON_PROPORTION.SMALL}
          disabled={isSaving}>
          Save
        </Button>
      </div>
    </form>
  )
}
